import React from 'react';

import { Button, Divider, IconButton, TitleWithDescription } from '@infoods/lifebase-ui';
import AccessibilityNewRoundedIcon from '@mui/icons-material/AccessibilityNewRounded';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';

import classes from './WelcomeCard.module.scss';
import { HelpCenterResourceRow } from './HelpCenterResourceRow/HelpCenterResourceRow';
import { HELP_CENTER_LINK } from '../../../constants/helpCenterLinks';
import { goTo } from '../../../../shared/utils/angularUtils';
import { openNewTab } from '../../../../shared/utils/windowUtils';

type HelpCenterAction =
  | { type: 'EXTERNAL'; url: string }
  | { type: 'INTERNAL'; path: string };

type WelcomeCardProps = {
  onClose: () => void;
  onStartChat: () => void;
};

const WelcomeCard = ({ onClose, onStartChat }: WelcomeCardProps) => {
  // openNewTab keeps the user on the page, so only in-app navigation closes the panel
  const handleActionClick = (action: HelpCenterAction) => {
    if (action.type === 'EXTERNAL') {
      openNewTab(action.url);
      return;
    }

    onClose();
    goTo(action.path);
  };

  return (
    <div>
      <div className={classes.header}>
        <div className={classes.titleRow}>
          <h2 className={classes.mainTitle}>Help & Support</h2>
          <IconButton aria-label="Close help center" onClick={onClose}>
            <CloseRoundedIcon />
          </IconButton>
        </div>
        <p className={classes.subtitle}>
          Get a fast answer, contact support, or open the exact resource you need.
        </p>
      </div>
      <Divider flexItem />
      <div className={classes.welcome}>
        <TitleWithDescription
          title="How can we help?"
          titleFont="title-16-bold"
          description="Start with the AI help bot for an immediate answer. Submit a ticket when you need a person."
          descriptionColor="neutral-600"
        />
        <div className={classes.welcomeActions}>
          <Button variant="contained" size="medium" onClick={onStartChat}>
            Start Chat
          </Button>
          <Button
            variant="outlined"
            size="medium"
            onClick={() =>
              handleActionClick({
                type: 'EXTERNAL',
                url: HELP_CENTER_LINK.SUBMIT_TICKET,
              })
            }
          >
            Submit Ticket
          </Button>
        </div>
      </div>
      <Divider flexItem />
      <p className={classes.sectionLabel}>POPULAR RESOURCES</p>
      <Divider flexItem />
      <HelpCenterResourceRow
        title="InBody Machine & Data"
        description="Fix machine connection or scan-data upload issues"
        icon={<AccessibilityNewRoundedIcon fontSize="large" color="inherit" />}
        iconColor="error"
        onClick={() =>
          handleActionClick({
            type: 'EXTERNAL',
            url: HELP_CENTER_LINK.INBODY_MACHINE_AND_DATA,
          })
        }
      />
      <Divider flexItem />
      <HelpCenterResourceRow
        title="Results Guide"
        description="Understand key InBody results and explain them clearly"
        icon={<FolderOpenRoundedIcon fontSize="large" color="inherit" />}
        iconColor="warning"
        onClick={() =>
          handleActionClick({
            type: 'EXTERNAL',
            url: HELP_CENTER_LINK.RESULTS_GUIDE,
          })
        }
      />
      <Divider flexItem />
      <HelpCenterResourceRow
        title="SWYMO Academy"
        description="Videos and resources related to the SWYMO platform"
        icon={<SchoolOutlinedIcon fontSize="large" color="inherit" />}
        iconColor="violet"
        onClick={() =>
          handleActionClick({
            type: 'INTERNAL',
            path: '/resources-library/swymo-academy',
          })
        }
      />
      <Divider flexItem />
      <HelpCenterResourceRow
        title="Business Center Resources"
        description="All videos and resources related to your CRM"
        icon={<BusinessCenterOutlinedIcon fontSize="large" color="inherit" />}
        iconColor="success"
        onClick={() =>
          handleActionClick({
            type: 'INTERNAL',
            path: '/resources-library/business-center',
          })
        }
      />
      <Divider flexItem />
      <p className={classes.footer}>
        Can not find it? Start a chat and we will point you in the right direction.
      </p>
    </div>
  );
};

export { WelcomeCard };
